import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { LOADING_STATE } from '~/constants/LoadingState';
import { TAB } from '~/constants/Tab';

import { camelcaseKeysFromApi } from '@vestigas/data';

import LocalStorageService from '~/services/localStorage.service';

import {
  SELECTABLE_COLUMNS,
  selectableReportFieldHeaderNames,
} from '~/modules/dashboard/components/DashboardReport/constants';

import { dateUtils } from '~/utils/dateUtils';
import { log } from '~/utils/logging';

import {
  changesFilterGroup,
  unassignedDeliveryNotesFilterGroup,
} from '~/components/deliveries/deliveryOverview/DeliveryFilterGroups/utils/filterGroups/fixTabsOrder';

import { getStoredUuidList } from './getStoredUuidList';

const filtersSlice = createSlice({
  initialState: {
    dashboard_filterGroups:
      LocalStorageService.getObjectFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FILTER_GROUPS,
      ) ?? [],

    dashboard_filterRows:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FILTER_ROWS,
      ) ?? [],

    dashboard_individualDateRange:
      LocalStorageService.getBooleanFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_INDIVIDUAL_DATE_RANGE,
        false,
      ),

    dashboard_selectableColumns: [],
    dashboard_selectedColumns: (() => {
      const selectedColumns =
        LocalStorageService.getArrayFromLocalStorage(
          LocalStorageService.FILTER_DASHBOARD_SELECTED_COLUMNS,
        ) ?? [];

      // For a reason we cannot find at the moment, the selected columns sometimes contain the header names instead of the field names.
      // ( Might be some old version use, like someone did not update their cached version, as was noticed several times in Sentry )
      // FIXME: This is a workaround to convert the header names to the field names.
      return selectedColumns.map((column) => {
        if (selectableReportFieldHeaderNames.has(column)) {
          return SELECTABLE_COLUMNS.find(
            ({ headerName }) => headerName === column,
          )?.field;
        }

        return column;
      });
    })(),

    // If no filter group is currently selected, only "Geliefert" dlns should be displayed in the Dashboard
    dashboard_selectedAcceptState:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ACCEPT_STATE,
      ) ?? [],

    dashboard_selectedArticle:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ARTICLE,
      ) ?? [],

    dashboard_selectedArticleNumber:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ARTICLE_NUMBER,
      ) ?? [],

    dashboard_selectedCategory:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CATEGORY,
      ) ?? [],

    dashboard_selectedCostCenter:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_COST_CENTER,
      ) ?? [],

    dashboard_selectedCustomerNumber:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CUSTOMER_NUMBER,
      ) ?? [],

    dashboard_selectedCustomFields:
      LocalStorageService.getObjectFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CUSTOM_FIELDS,
      ) ?? [],

    dashboard_selectedDateRange: dateUtils.getDateRangeFromLocalStorage(
      LocalStorageService.FILTER_DASHBOARD_DATE_RANGE,
      LocalStorageService.getBooleanFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_INDIVIDUAL_DATE_RANGE,
        false,
      ),
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',
    ),

    dashboard_selectedFilterGroup:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SELECTED_FILTER_GROUP,
      ) ?? null,

    dashboard_selectedFromSite:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FROM_SITE,
      ) ?? [],

    dashboard_selectedOuId:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_OU_ID,
      ) ?? [],

    dashboard_selectedPermittedCostCenters:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PERMITTED_COST_CENTERS,
      ) ?? [],

    dashboard_selectedPermittedToSites:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PERMITTED_TO_SITES,
      ) ?? [],

    dashboard_selectedPredefinedDateRange:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',

    dashboard_selectedProcessState:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PROCESS_STATE,
      ) ?? [],

    dashboard_selectedRecipient:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_RECIPIENT,
      ) ?? [],

    dashboard_selectedSettledStatus:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SETTLED_STATUS,
      ) ?? [],

    dashboard_selectedSupplier:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SUPPLIER,
      ) ?? [],

    dashboard_selectedToSiteRecipient:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_RECIPIENT,
      ) ?? [],

    dashboard_selectedToSiteSupplier:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_SUPPLIER,
      ) ?? [],

    dashboard_selectedToSiteSupplierTradeContact:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_SUPPLIER_TRADE_CONTACT,
      ) ?? [],

    dashboard_selectedUnit:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_UNIT,
      ) ?? 'EA',

    dashboard_tab: TAB.DASHBOARD.CHARTS.INDEX,

    dataExchanges_query: '',

    dataExchanges_selectedCompany:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DATA_EXCHANGES_COMPANY,
      ) ?? null,

    deliveries: {
      changes: {
        sortModel: [],
      },
      dateRange: {
        isCustom: LocalStorageService.getBooleanFromLocalStorage(
          LocalStorageService.FILTER_DELIVERY_INDIVIDUAL_DATE_RANGE,
          true,
        ),
        predefined:
          LocalStorageService.getLocalStorage(
            LocalStorageService.FILTER_DELIVERY_PREDEFINED_DATE_RANGE,
          ) ?? '30-days',
      },
      filterGroups:
        LocalStorageService.getObjectFromLocalStorage(
          LocalStorageService.FILTER_DELIVERY_FILTER_GROUPS,
        ) ?? [],
      filterRows:
        LocalStorageService.getArrayFromLocalStorage(
          LocalStorageService.FILTER_DELIVERY_FILTER_ROWS,
        ) ?? [],
      selected: {
        filter: {
          acceptStates:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_ACCEPT_STATE,
            ) ?? [],
          articleNumbers:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_ARTICLE_NUMBER,
            ) ?? [],
          articles:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_ARTICLE,
            ) ?? [],
          costCenters:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_COST_CENTER,
            ) ?? [],
          customerNumber:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_CUSTOMER_NUMBER,
            ) ?? [],
          customFields:
            LocalStorageService.getObjectFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_CUSTOM_FIELDS,
            ) ?? [],
          dateRange: dateUtils.getDateRangeFromLocalStorage(
            LocalStorageService.FILTER_DELIVERY_DATE_RANGE,
            LocalStorageService.getBooleanFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_INDIVIDUAL_DATE_RANGE,
              false,
            ),
            LocalStorageService.getLocalStorage(
              LocalStorageService.FILTER_DELIVERY_PREDEFINED_DATE_RANGE,
            ) ?? '30-days',
          ),
          fromSites:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_FROM_SITE,
            ) ?? [],
          permittedCostCenters:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_PERMITTED_COST_CENTERS,
            ) ?? [],
          permittedToSites:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_PERMITTED_TO_SITES,
            ) ?? [],
          processStates:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_PROCESS_STATE,
            ) ?? [],
          query: '',
          recipients:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_RECIPIENT,
            ) ?? [],
          settledStatuses:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_SETTLED_STATUS,
            ) ?? [],
          suppliers:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_SUPPLIER,
            ) ?? [],
          toSiteRecipients:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_SITE_RECIPIENT,
            ) ?? [],
          toSiteSuppliers:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.FILTER_DELIVERY_SITE_SUPPLIER,
            ) ?? [],
        },
        filterGroup: {
          id:
            LocalStorageService.getLocalStorage(
              LocalStorageService.DELIVERY_SELECTED_FILTER_GROUP,
            ) ?? '',
          isOpen: LocalStorageService.getBooleanFromLocalStorage(
            LocalStorageService.FILTER_DELIVERY_FILTER_GROUP_OPEN,
            false,
          ),
        },
        sortModel: [
          {
            field: 'createdOn',
            sort: 'desc',
          },
        ],
      },
      selectField: 'all',
    },

    delivery_dateRange: dateUtils.getDateRangeFromLocalStorage(
      LocalStorageService.FILTER_DELIVERY_DATE_RANGE,
      LocalStorageService.getBooleanFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_INDIVIDUAL_DATE_RANGE,
        false,
      ),
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',
    ),

    delivery_filterGroupOpen: LocalStorageService.getBooleanFromLocalStorage(
      LocalStorageService.FILTER_DELIVERY_FILTER_GROUP_OPEN,
      false,
    ),

    delivery_filterGroups:
      LocalStorageService.getObjectFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FILTER_GROUPS,
      ) ?? [],

    delivery_filterRows: LocalStorageService.getArrayFromLocalStorage(
      LocalStorageService.FILTER_DELIVERY_FILTER_ROWS,
    )?.length
      ? LocalStorageService.getArrayFromLocalStorage(
          LocalStorageService.FILTER_DELIVERY_FILTER_ROWS,
        )
      : ['selectedProcessState'],

    delivery_individualDateRange:
      LocalStorageService.getBooleanFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_INDIVIDUAL_DATE_RANGE,
        false,
      ),

    delivery_predefinedDateRange:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',

    delivery_query: '',

    delivery_selectedAcceptState:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ACCEPT_STATE,
      ) ?? [],

    delivery_selectedArticle:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ARTICLE,
      ) ?? [],

    delivery_selectedArticleNumber:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ARTICLE_NUMBER,
      ) ?? [],

    delivery_selectedCategory:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CATEGORY,
      ) ?? [],

    delivery_selectedCostCenter:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_COST_CENTER,
      ) ?? [],

    delivery_selectedCustomerNumber:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CUSTOMER_NUMBER,
      ) ?? [],

    delivery_selectedCustomFields:
      LocalStorageService.getObjectFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CUSTOM_FIELDS,
      ) ?? [],

    delivery_selectedFilterGroup:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SELECTED_FILTER_GROUP,
      ) ?? null,

    delivery_selectedFromSite:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FROM_SITE,
      ) ?? [],

    delivery_selectedNumber:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_NUMBER,
      ) ?? [],

    delivery_selectedPermittedCostCenters:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PERMITTED_COST_CENTERS,
      ) ?? [],

    delivery_selectedPermittedToSites:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PERMITTED_TO_SITES,
      ) ?? [],

    delivery_selectedProcessState:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PROCESS_STATE,
      ) ?? [],

    delivery_selectedRecipient:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_RECIPIENT,
      ) ?? [],

    delivery_selectedSettledStatus:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SETTLED_STATUS,
      ) ?? [],

    delivery_selectedSupplier:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SUPPLIER,
      ) ?? [],

    delivery_selectedToSiteRecipient:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SITE_RECIPIENT,
      ) ?? [],

    delivery_selectedToSiteSupplier:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SITE_SUPPLIER,
      ) ?? [],

    delivery_selectedSource:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SOURCE,
      ) ?? [],

    delivery_selectedSubmittedByUserId:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SUBMITTED_BY_USER_ID,
      ) ?? [],

    delivery_selectedOuId:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FILTER_OU_ID,
      ) ?? [],

    delivery_selectField: 'all',

    // filters of delivery overview component
    delivery_tab:
      LocalStorageService.getNumberFromLocalStorage(
        LocalStorageService.FILTER_DELIVERY_TAB,
      ) ?? TAB.DELIVERY.LIST.INDEX,

    deliveryList_sortModel: [
      {
        field: 'createdOn',
        sort: 'desc',
      },
    ],

    global: {
      selected: {
        filter: {
          costCenters:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.SELECTED_COST_CENTERS,
            ) ?? [],
          sites:
            LocalStorageService.getArrayFromLocalStorage(
              LocalStorageService.SELECTED_SITES,
            ) ?? [],
        },
      },
    },

    home_individualDateRange: LocalStorageService.getBooleanFromLocalStorage(
      LocalStorageService.FILTER_HOME_INDIVIDUAL_DATE_RANGE,
      false,
    ),

    home_selectedDateRange: dateUtils.getDateRangeFromLocalStorage(
      LocalStorageService.FILTER_HOME_DATE_RANGE,
      LocalStorageService.getBooleanFromLocalStorage(
        LocalStorageService.FILTER_HOME_INDIVIDUAL_DATE_RANGE,
        false,
      ),
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_HOME_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',
    ),

    home_selectedPredefinedDateRange:
      LocalStorageService.getLocalStorage(
        LocalStorageService.FILTER_HOME_PREDEFINED_DATE_RANGE,
      ) ?? '30-days',

    // filters of home screen
    home_selectedUnit: null,

    // date of the oldest filtered delivery note in the webapp
    oldestFilteredDlnDate: null,

    oldestFilteredDlnDateLoading: LOADING_STATE.LOADING,

    selectedCostCenters: getStoredUuidList(
      LocalStorageService.SELECTED_COST_CENTERS,
    ),

    // sites and cost centers selection
    selectedSites: getStoredUuidList(LocalStorageService.SELECTED_SITES),

    suppliers_responsible_people:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DATA_SUPPLIERS_RESPONSIBLE_PEOPLE,
      ) ?? [],

    // new suppliers data store
    suppliers_selectedCompanies:
      LocalStorageService.getArrayFromLocalStorage(
        LocalStorageService.FILTER_DATA_SELECTED_SUPPLIERS_COMPANY,
      ) ?? [],
    suppliers_sort_model: LocalStorageService.getObjectFromLocalStorage(
      LocalStorageService.FILTER_DATA_SUPPLIERS_SORT_MODEL,
    ) ?? {
      sortDirection: 'ASC',
      sortKey: 'alphabetically',
    },
  },
  name: 'filters',
  reducers: {
    /**
     * Updates the selected filter group for the provided page.
     *
     * @param {Object} state - The current state object.
     * @param {Object} payload - The payload object containing page, groupId, and isOpen.
     * @param {string} payload.page - The page to update the filter group for.
     * @param {string} payload.groupId - The ID of the filter group to set.
     * @param {boolean} payload.isOpen - The open state of the filter group.
     * @return {void} No return value.
     */
    selectFilterGroup(state, { payload: { groupId, isOpen = true, page } }) {
      if (!state[page] || groupId === undefined) {
        log.error({
          message: `Invalid selectFilterGroup payload: ${page}/${groupId} (isOpen: ${isOpen})`,
          error: new InvalidDateException(
            `Invalid selectFilterGroup payload: ${page}/${groupId} (isOpen: ${isOpen})`,
          ),
        });
        return;
      }

      state[page].selected.filterGroup.id = groupId;
      state[page].selected.filterGroup.isOpen = groupId ? isOpen : false;

      // TODO: handle updating the selected filter values based on the new filter group
    },

    setDashboard_filterGroups(state, parameter) {
      const payload = camelcaseKeysFromApi(parameter.payload);

      state.dashboard_filterGroups = payload;
      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FILTER_GROUPS,
        payload,
      );
    },

    setDashboard_filterRows(state, parameter) {
      const { payload } = parameter;
      state.dashboard_filterRows = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FILTER_ROWS,
        payload,
      );
    },

    setDashboard_individualDateRange(state, parameter) {
      const { payload } = parameter;
      state.dashboard_individualDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_INDIVIDUAL_DATE_RANGE,
        payload,
      );
    },

    setDashboard_predefinedDateRange(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedPredefinedDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PREDEFINED_DATE_RANGE,
        payload,
      );
    },

    setDashboard_selectableColumns(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectableColumns = payload;
      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SELECTABLE_COLUMNS,
        payload,
      );
    },

    setDashboard_selectedAcceptState(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedAcceptState = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ACCEPT_STATE,
        payload,
      );
    },

    setDashboard_selectedArticle(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedArticle = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ARTICLE,
        payload,
      );
    },

    setDashboard_selectedArticleNumber(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedArticleNumber = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_ARTICLE_NUMBER,
        payload,
      );
    },

    setDashboard_selectedCategory(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedCategory = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CATEGORY,
        payload,
      );
    },

    setDashboard_selectedColumns(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedColumns = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SELECTED_COLUMNS,
        payload,
      );
    },

    setDashboard_selectedCostCenter(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedCostCenter = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_COST_CENTER,
        payload,
      );
    },

    setDashboard_selectedCustomerNumber(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedCustomerNumber = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CUSTOMER_NUMBER,
        payload,
      );
    },

    setDashboard_selectedCustomFields(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedCustomFields = payload;
      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_CUSTOM_FIELDS,
        payload,
      );
    },

    setDashboard_selectedDateRange(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedDateRange = payload;
      dateUtils.setDateRangeInLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_DATE_RANGE,
        payload,
      );
    },

    setDashboard_selectedFilterGroup(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedFilterGroup = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SELECTED_FILTER_GROUP,
        payload,
      );
    },

    setDashboard_selectedFromSite(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedFromSite = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_FROM_SITE,
        payload,
      );
    },

    setDashboard_selectedOuId(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedOuId = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_OU_ID,
        payload,
      );
    },

    setDashboard_selectedPermittedCostCenters(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedPermittedCostCenters = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PERMITTED_COST_CENTERS,
        payload,
      );
    },

    setDashboard_selectedPermittedToSites(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedPermittedToSites = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PERMITTED_TO_SITES,
        payload,
      );
    },

    setDashboard_selectedProcessState(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedProcessState = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_PROCESS_STATE,
        payload,
      );
    },

    setDashboard_selectedRecipient(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedRecipient = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_RECIPIENT,
        payload,
      );
    },

    setDashboard_selectedSettledStatus(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedSettledStatus = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SETTLED_STATUS,
        payload,
      );
    },

    setDashboard_selectedSupplier(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedSupplier = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SUPPLIER,
        payload,
      );
    },

    setDashboard_selectedToSiteRecipient(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedToSiteRecipient = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_RECIPIENT,
        payload,
      );
    },

    setDashboard_selectedToSiteSupplier(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedToSiteSupplier = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_SUPPLIER,
        payload,
      );
    },

    setDashboard_selectedToSiteSupplierTradeContact(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedToSiteSupplierTradeContact = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_SITE_SUPPLIER_TRADE_CONTACT,
        payload,
      );
    },

    setDashboard_selectedUnit(state, parameter) {
      const { payload } = parameter;
      state.dashboard_selectedUnit = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DASHBOARD_UNIT,
        payload,
      );
    },

    // actions of dashboard overview component
    setDashboard_tab(state, parameter) {
      const { payload } = parameter;
      state.dashboard_tab = payload;
    },

    setDataExchanges_query(state, parameter) {
      const { payload } = parameter;
      state.dataExchanges_query = payload;
    },

    setDataExchanges_selectedCompany(state, parameter) {
      const { payload } = parameter;
      state.dataExchanges_selectedCompany = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DATA_EXCHANGES_COMPANY,
        payload,
      );
    },

    setDelivery_dateRange(state, parameter) {
      const { payload } = parameter;
      if (!payload.dateRange) {
        log.error({
          message: `Invalid date range: ${payload.dateRange}`,
          error: new InvalidDateException(
            `Invalid date range: ${payload.dateRange}`,
          ),
        });
      }

      state.delivery_dateRange = payload.dateRange;
      if (!payload.updateCookie) {
        return;
      }

      dateUtils.setDateRangeInLocalStorage(
        LocalStorageService.FILTER_DELIVERY_DATE_RANGE,
        payload.dateRange,
      );
    },

    setDelivery_filterGroupOpen(state, parameter) {
      const { payload } = parameter;
      state.delivery_filterGroupOpen = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FILTER_GROUP_OPEN,
        payload,
      );
    },

    setDelivery_filterGroups(state, parameter) {
      const payload = camelcaseKeysFromApi(parameter.payload).map(
        ({ id, ...rest }) => ({
          ...rest,
          id: id ?? uuidv4(), // make sure every filter group has an id
        }),
      );

      // Remove changesFilterGroup if it exists and always insert it as second filter group
      const changesFilterGroupIndex = payload.findIndex(
        ({ name }) =>
          name.toLowerCase() === changesFilterGroup.name.toLowerCase(),
      );
      if (changesFilterGroupIndex !== -1) {
        payload.splice(changesFilterGroupIndex, 1);
      }

      payload.splice(1, 0, changesFilterGroup);

      // Remove unassignedDeliveryNotesFilterGroup if it exists (by comparing filter group names) and insert unassignedDeliveryNotesFilterGroup as third filter group, if it existed
      const unassignedDeliveryNotesFilterGroupIndex = payload.findIndex(
        ({ name }) =>
          name.toLowerCase() ===
          unassignedDeliveryNotesFilterGroup.name.toLowerCase(),
      );
      if (unassignedDeliveryNotesFilterGroupIndex !== -1) {
        payload.splice(unassignedDeliveryNotesFilterGroupIndex, 1);
        payload.splice(2, 0, unassignedDeliveryNotesFilterGroup);
      }

      state.delivery_filterGroups = payload;

      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FILTER_GROUPS,
        payload,
      );
    },

    setDelivery_filterRows(state, parameter) {
      const { payload } = parameter;
      if (payload.length === 0) return;

      state.delivery_filterRows = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FILTER_ROWS,
        payload,
      );
    },

    setDelivery_individualDateRange(state, parameter) {
      const { payload } = parameter;
      state.delivery_individualDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DELIVERY_INDIVIDUAL_DATE_RANGE,
        payload,
      );
    },

    setDelivery_predefinedDateRange(state, parameter) {
      const { payload } = parameter;
      state.delivery_predefinedDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PREDEFINED_DATE_RANGE,
        payload,
      );
    },

    setDelivery_query(state, parameter) {
      const { payload } = parameter;
      state.delivery_query = payload;
    },

    setDelivery_selectedAcceptState(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedAcceptState = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ACCEPT_STATE,
        payload,
      );
    },

    setDelivery_selectedArticle(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedArticle = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ARTICLE,
        payload,
      );
    },

    setDelivery_selectedArticleNumber(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedArticleNumber = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_ARTICLE_NUMBER,
        payload,
      );
    },

    setDelivery_selectedCategory(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedCategory = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CATEGORY,
        payload,
      );
    },

    setDelivery_selectedCostCenter(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedCostCenter = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_COST_CENTER,
        payload,
      );
    },

    setDelivery_selectedCustomFields(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedCustomFields = payload;
      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CUSTOM_FIELDS,
        payload,
      );
    },

    setDelivery_selectedCustomerNumber(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedCustomerNumber = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_CUSTOMER_NUMBER,
        payload,
      );
    },

    setDelivery_selectedFilterGroup(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedFilterGroup = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SELECTED_FILTER_GROUP,
        payload,
      );
    },

    setDelivery_selectedFromSite(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedFromSite = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_FROM_SITE,
        payload,
      );
    },

    setDelivery_selectedNumber(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedNumber = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_NUMBER,
        payload,
      );
    },

    setDelivery_selectedOuId(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedOuId = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_OU_ID,
        payload,
      );
    },

    setDelivery_selectedPermittedCostCenters(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedPermittedCostCenters = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PERMITTED_COST_CENTERS,
        payload,
      );
    },

    setDelivery_selectedPermittedToSites(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedPermittedToSites = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PERMITTED_TO_SITES,
        payload,
      );
    },

    setDelivery_selectedProcessState(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedProcessState = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_PROCESS_STATE,
        payload,
      );
    },

    setDelivery_selectedRecipient(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedRecipient = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_RECIPIENT,
        payload,
      );
    },

    setDelivery_selectedSettledStatus(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedSettledStatus = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SETTLED_STATUS,
        payload,
      );
    },

    setDelivery_selectedSupplier(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedSupplier = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SUPPLIER,
        payload,
      );
    },

    setDelivery_selectedToSiteRecipient(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedToSiteRecipient = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SITE_RECIPIENT,
        payload,
      );
    },

    setDelivery_selectedToSiteSupplier(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedToSiteSupplier = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SITE_SUPPLIER,
        payload,
      );
    },

    setDelivery_selectedSource(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedSource = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SOURCE,
        payload,
      );
    },

    setDelivery_selectedSubmittedByUserId(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectedSubmittedByUserId = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DELIVERY_SUBMITTED_BY_USER_ID,
        payload,
      );
    },

    setDelivery_selectField(state, parameter) {
      const { payload } = parameter;
      state.delivery_selectField = payload;
    },

    // actions of delivery overview component
    setDelivery_tab(state, parameter) {
      const { payload } = parameter;
      state.delivery_tab = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_DELIVERY_TAB,
        payload,
      );
    },

    setDeliveryList_sortModel(state, parameter) {
      const { payload } = parameter;
      state.deliveryList_sortModel = payload;
    },

    // -----------------------------------------------------------------------------------------------------------------
    /**
     * Updates the filter value for the provided page, filter key, and filter value.
     *
     * @example: setFilterValue({ { page: 'deliveries', filterKey: 'selectedUnit', filterValue: 'm3' } })
     *
     * @param {Object} state - The current state object.
     * @param {string} payload.page - The page to update the filter value for: 'deliveries' | 'dashboard' | 'home'.
     * @param {string} payload.filterKey - The key representing the filter, e.g. articleNumbers, suppliers
     * @param {any} payload.filterValue - The new value for the filter. Replaces the old value.
     * @param {Object} payload.options - Additional options you want to pass to the reducer.
     * @return {void} No return value.
     */
    setFilterValue(
      state,
      { payload: { filterKey, filterValue, options = {}, page } },
    ) {
      if (state[page]?.selected?.filter?.[filterKey] === undefined) {
        log.error({
          message: `Invalid setFilterValue payload: ${page}/${filterKey} -> ${filterValue}`,
          error: new InvalidDateException(
            `Invalid setFilterValue payload: ${page}/${filterKey} -> ${filterValue}`,
          ),
        });
        return;
      }

      // Set the new value(s) to the selected filter.
      state[page].selected.filter[filterKey] = filterValue;
    },

    setHome_individualDateRange(state, parameter) {
      const { payload } = parameter;
      state.home_individualDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_HOME_INDIVIDUAL_DATE_RANGE,
        payload,
      );
    },

    setHome_selectedDateRange(state, parameter) {
      const { payload } = parameter;
      state.home_selectedDateRange = payload;
      dateUtils.setDateRangeInLocalStorage(
        LocalStorageService.FILTER_HOME_DATE_RANGE,
        payload,
      );
    },

    setHome_selectedPredefinedDateRange(state, parameter) {
      const { payload } = parameter;
      state.home_selectedPredefinedDateRange = payload;
      LocalStorageService.setLocalStorage(
        LocalStorageService.FILTER_HOME_PREDEFINED_DATE_RANGE,
        payload,
      );
    },

    setSelectedCostCenters(state, parameter) {
      const { payload } = parameter;
      state.selectedCostCenters = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.SELECTED_COST_CENTERS,
        payload,
      );
    },

    // -----------------------------------------------------------------------------------------------------------------
    // actions of sites and cost centers selection
    setSelectedSites(state, parameter) {
      const { payload } = parameter;
      state.selectedSites = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.SELECTED_SITES,
        payload,
      );
    },

    setSuppliers_responsiblePeople(state, parameter) {
      const { payload } = parameter;
      state.suppliers_responsible_people = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DATA_SUPPLIERS_RESPONSIBLE_PEOPLE,
        payload,
      );
    },

    setSuppliers_selectCompany(state, parameter) {
      const { payload } = parameter;
      state.suppliers_selectedCompanies = payload;
      LocalStorageService.setArrayAsLocalStorage(
        LocalStorageService.FILTER_DATA_SELECTED_SUPPLIERS_COMPANY,
        payload,
      );
    },
    setSuppliers_sortModel(state, parameter) {
      const { payload } = parameter;
      state.suppliers_sort_model = payload;
      LocalStorageService.setObjectAsLocalStorage(
        LocalStorageService.FILTER_DATA_SUPPLIERS_SORT_MODEL,
        payload,
      );
    },
  },
});

const { actions, reducer } = filtersSlice;
export const {
  selectFilterGroup,
  setDashboard_filterGroups,
  setDashboard_filterRows,
  setDashboard_individualDateRange,
  setDashboard_predefinedDateRange,
  setDashboard_selectableColumns,
  setDashboard_selectedAcceptState,
  setDashboard_selectedArticle,
  setDashboard_selectedArticleNumber,
  setDashboard_selectedCategory,
  setDashboard_selectedColumns,
  setDashboard_selectedCostCenter,
  setDashboard_selectedCustomerNumber,
  setDashboard_selectedCustomFields,
  setDashboard_selectedDateRange,
  setDashboard_selectedFilterGroup,
  setDashboard_selectedFromSite,
  setDashboard_selectedOuId,
  setDashboard_selectedPermittedCostCenters,
  setDashboard_selectedPermittedToSites,
  setDashboard_selectedProcessState,
  setDashboard_selectedRecipient,
  setDashboard_selectedSettledStatus,
  setDashboard_selectedSupplier,
  setDashboard_selectedToSiteRecipient,
  setDashboard_selectedToSiteSupplier,
  setDashboard_selectedToSiteSupplierTradeContact,
  setDashboard_selectedUnit,
  setDashboard_tab,
  setDataExchanges_query,
  setDataExchanges_selectedCompany,
  setDelivery_dateRange,
  setDelivery_filterGroupOpen,
  setDelivery_filterGroups,
  setDelivery_filterRows,
  setDelivery_individualDateRange,
  setDelivery_predefinedDateRange,
  setDelivery_query,
  setDelivery_selectedAcceptState,
  setDelivery_selectedArticle,
  setDelivery_selectedArticleNumber,
  setDelivery_selectedCategory,
  setDelivery_selectedCostCenter,
  setDelivery_selectedCustomerNumber,
  setDelivery_selectedCustomFields,
  setDelivery_selectedFilterGroup,
  setDelivery_selectedFromSite,
  setDelivery_selectedNumber,
  setDelivery_selectedOuId,
  setDelivery_selectedPermittedCostCenters,
  setDelivery_selectedPermittedToSites,
  setDelivery_selectedProcessState,
  setDelivery_selectedRecipient,
  setDelivery_selectedSettledStatus,
  setDelivery_selectedSource,
  setDelivery_selectedSubmittedByUserId,
  setDelivery_selectedSupplier,
  setDelivery_selectedToSiteRecipient,
  setDelivery_selectedToSiteSupplier,
  setDelivery_selectField,
  setDelivery_tab,
  setDeliveryList_sortModel,
  setFilterValue,
  setHome_individualDateRange,
  setHome_selectedDateRange,
  setHome_selectedPredefinedDateRange,
  setSelectedCostCenters,
  setSelectedSites,
  setSuppliers_responsiblePeople,
  setSuppliers_selectCompany,
  setSuppliers_sortModel,
} = actions;

export default reducer;
